import {
  Icon,
  ProgressCircle,
  Switch,
} from "@smart/itops-sb-design-system-dom";
import { useLoadSetting, useUpdateSetting } from "@smart/manage-gql-client-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

const SectionContent = [
  {
    title: "To use voice features:",
    items: [
      "Clients must grant microphone permission in their browser.",
      'When enabled, a "Use Voice" option will appear on forms.',
      'Clients click "Use Voice" to begin.',
    ],
  },
  {
    title: "How it works:",
    items: [
      "The voice assistant reads each question aloud.",
      "Clients respond using their voice.",
      "Responses are transcribed and entered into the form automatically.",
      "The assistant listens only while voice mode is active.",
    ],
  },
  {
    title: "Review and edit:",
    items: [
      "Clients can review and edit their answers at any time.",
      "All responses can be reviewed before submitting the form.",
    ],
  },
];

const InfoSection = ({ title, items }: { title: string; items: string[] }) => (
  <div className="flex flex-col gap-[0.4rem]">
    <h4 className="font-semibold">{title}</h4>
    <ul className="list-disc pl-5 space-y-1">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </div>
);

export const VoiceGuideSettings = () => {
  const { publishEvent } = useAnalyticsEvent();
  const { result: setting, loading: loadingSetting } = useLoadSetting({});
  const [update] = useUpdateSetting();

  const voiceGuidedFormsEnabled = setting?.voiceGuide?.enabled ?? false;

  if (loadingSetting) {
    return <ProgressCircle />;
  }

  return (
    <div className="flex flex-col px-[2.4rem] py-[1.8rem] gap-medium h-full overflow-y-auto">
      <div className="flex flex-col gap-small">
        <Switch
          dataTestId="enable-voice-guided-forms-toggle"
          toggled={voiceGuidedFormsEnabled}
          onClick={() => {
            publishEvent?.({
              category: "track",
              name: voiceGuidedFormsEnabled
                ? "VoiceFillDisabled"
                : "VoiceFillEnabled",
              screen: "VoiceGuide",
              entryPoint: "FirmSettingsIntakeForms",
              component: "Voice-Guided Toggle",
              jiraKey: voiceGuidedFormsEnabled ? "LINK-3321" : "LINK-3347",
              interactionType: "Active",
            }).catch(console.error);

            update({
              variables: {
                fields: {
                  voiceGuide: { enabled: !voiceGuidedFormsEnabled },
                },
              },
            })?.catch(console.error);
          }}
          text={
            <span className="text-ui font-semibold text-neutral-850">
              Enable Voice-Guided Forms
            </span>
          }
        />
        <div className="flex flex-col gap-[0.2rem] ml-[4.4rem] text-caption">
          <p>
            Allow forms to be read aloud and completed through spoken responses
          </p>
          <p>
            When enabled firms will see the Voice button in the preview of the
            forms, when disabled this will not be shown
          </p>
          <div className="flex items-center gap-small text-neutral-600">
            <Icon
              name="regularCircleExclamation"
              className="w-[1.2rem] h-[1.2rem]"
            />
            <span>Audio is not stored by Smokeball.</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-medium rounded p-[1.6rem] bg-neutral-20 text-neutral-850">
        <h3 className="text-ui font-semibold">To Use Voice-Guided Forms</h3>
        {SectionContent.map((section) => (
          <InfoSection key={section.title} {...section} />
        ))}
      </div>
    </div>
  );
};
